/**
 * Credit-monitor read contract (router#2014, epic #1997 E5).
 *
 * The ops page is an expansion of the #2007 overdue ladder, not a second
 * monitor. Totals are derived from the same account rows the table
 * renders — a separate aggregation would fork the numbers.
 *
 * Overdue / due-soon membership is a dunning verdict. Callers pass the
 * dates dunning already classified; this module does not look at
 * `billing_statements.due_date`. Current debt is live debt
 * `max(0, −balance)`, never a sum of `closing_ar`.
 *
 * Visibility: totals and rows are the caller's visible book (admin and
 * finance unrestricted; a sales session is own-book). A platform-wide
 * exposure figure for some roles is an open product question — do not
 * default it to unscoped.
 *
 * Dates are UTC+8 calendar 'YYYY-MM-DD'. Money is integer micro-USD.
 * Classify / sort / summarize take explicit inputs — they do not read
 * the wall clock.
 */

import { calendarDaysBetween, type CreditOverdueExemptionView, type CreditOverdueStage } from './credit-overdue.ts';
import { type CreditLayerUsage } from './credit-ops-read.ts';
import { isCalendarDateYmd } from './temporary-credit.ts';

export const CREDIT_MONITOR_QUEUES = ['overdue', 'due_soon', 'high_usage', 'other'] as const;
export type CreditMonitorQueue = (typeof CREDIT_MONITOR_QUEUES)[number];

export const CREDIT_MONITOR_FILTERS = [
  'all',
  'overdue',
  'due_soon',
  'temporary_expiring',
] as const;
export type CreditMonitorFilter = (typeof CREDIT_MONITOR_FILTERS)[number];

export const CREDIT_MONITOR_SORTS = ['default', 'usage', 'name'] as const;
export type CreditMonitorSort = (typeof CREDIT_MONITOR_SORTS)[number];

/** 80% of the combined long-term + temporary cap, in basis points. */
export const CREDIT_MONITOR_HIGH_USAGE_RATIO_BPS = 8_000;

/**
 * Inclusive calendar-day window for "temporary credit will expire".
 * Matches the dunning due-soon horizon so the two todos share one clock.
 */
export const CREDIT_TEMPORARY_EXPIRING_HORIZON_DAYS = 7;

export function isCreditMonitorQueue(value: string): value is CreditMonitorQueue {
  for (const candidate of CREDIT_MONITOR_QUEUES) {
    if (candidate === value) return true;
  }
  return false;
}

export function isCreditMonitorFilter(value: string): value is CreditMonitorFilter {
  for (const candidate of CREDIT_MONITOR_FILTERS) {
    if (candidate === value) return true;
  }
  return false;
}

export function isCreditMonitorSort(value: string): value is CreditMonitorSort {
  for (const candidate of CREDIT_MONITOR_SORTS) {
    if (candidate === value) return true;
  }
  return false;
}

export interface CreditMonitorOverdueFact {
  readonly days_past_due: number;
  readonly due_date: string;
  readonly stage: CreditOverdueStage | null;
  readonly exemption: CreditOverdueExemptionView | null;
}

export interface CreditMonitorDueSoonFact {
  readonly due_date: string;
  readonly days_until_due: number;
}

export interface CreditMonitorAccountRow {
  readonly account_id: string;
  readonly org_id: string | null;
  readonly org_name: string | null;
  readonly layers: CreditLayerUsage;
  /** Live debt `max(0, −balance)`. Never a sum of `closing_ar`. */
  readonly live_debt_usd_micros: number;
  readonly queue: CreditMonitorQueue;
  /**
   * Set only when dunning reported `ar_overdue` for this account.
   * Null means not overdue — including a settled account whose open
   * statements still carry a historical `closing_ar`.
   */
  readonly overdue: CreditMonitorOverdueFact | null;
  readonly due_soon: CreditMonitorDueSoonFact | null;
  readonly exemption: CreditOverdueExemptionView | null;
  /** UTC+8 calendar date the active temporary grant ends, when inside the horizon. */
  readonly temporary_expires_on: string | null;
  readonly next_due_date: string | null;
}

export interface CreditMonitorSummary {
  readonly total_limit_usd_micros: number;
  readonly used_usd_micros: number;
  readonly overdue_usd_micros: number;
  readonly overdue_account_count: number;
  readonly due_soon_account_count: number;
  readonly temporary_expiring_account_count: number;
}

export interface GetCreditMonitorResponse {
  readonly summary: CreditMonitorSummary;
  readonly accounts: readonly CreditMonitorAccountRow[];
  readonly generated_at: string;
}

function requireCalendarDate(value: string, field: string): string {
  if (!isCalendarDateYmd(value)) {
    throw new Error(`${field} must be a UTC+8 calendar date YYYY-MM-DD`);
  }
  return value;
}

function requireSafeInteger(value: number, field: string): number {
  if (!Number.isSafeInteger(value)) {
    throw new Error(`${field} must be a safe integer`);
  }
  return value;
}

function requireNonNegativeSafeInteger(value: number, field: string): number {
  const parsed = requireSafeInteger(value, field);
  if (parsed < 0) {
    throw new Error(`${field} must be >= 0`);
  }
  return parsed;
}

function creditUsedUsdMicros(layers: CreditLayerUsage): number {
  return layers.long_term_used_usd_micros + layers.temporary_used_usd_micros;
}

/**
 * Combined long-term + temporary cap. Long-term and temporary grants are
 * mutually exclusive, so this is also the spendability line the gateway
 * reads from `accounts.credit_limit_usd_micros` (router#2061).
 */
export function creditCapUsdMicros(
  layers: Pick<CreditLayerUsage, 'long_term_cap_usd_micros' | 'temporary_cap_usd_micros'>,
): number {
  return layers.long_term_cap_usd_micros + layers.temporary_cap_usd_micros;
}

/**
 * High usage is over-limit, or used/cap at or above 80%, compared in
 * integer basis points so the threshold does not depend on float rounding.
 */
export function isCreditMonitorHighUsage(input: {
  readonly usedUsdMicros: number;
  readonly capUsdMicros: number;
  readonly overLimitUsdMicros: number;
}): boolean {
  const used = requireNonNegativeSafeInteger(input.usedUsdMicros, 'usedUsdMicros');
  const cap = requireNonNegativeSafeInteger(input.capUsdMicros, 'capUsdMicros');
  const overLimit = requireNonNegativeSafeInteger(
    input.overLimitUsdMicros,
    'overLimitUsdMicros',
  );
  if (overLimit > 0) return true;
  if (cap <= 0) return false;
  return used * 10_000 >= cap * CREDIT_MONITOR_HIGH_USAGE_RATIO_BPS;
}

export function classifyCreditMonitorQueue(input: {
  readonly overdue: boolean;
  readonly dueSoon: boolean;
  readonly usedUsdMicros: number;
  readonly capUsdMicros: number;
  readonly overLimitUsdMicros: number;
}): CreditMonitorQueue {
  if (input.overdue) return 'overdue';
  if (input.dueSoon) return 'due_soon';
  if (
    isCreditMonitorHighUsage({
      usedUsdMicros: input.usedUsdMicros,
      capUsdMicros: input.capUsdMicros,
      overLimitUsdMicros: input.overLimitUsdMicros,
    })
  ) {
    return 'high_usage';
  }
  return 'other';
}

/**
 * An active temporary grant expires inside the horizon when its UTC+8
 * calendar end date is today .. today+horizon inclusive.
 */
export function isTemporaryCreditExpiring(
  endsOn: string | null,
  today: string,
  horizonDays: number = CREDIT_TEMPORARY_EXPIRING_HORIZON_DAYS,
): boolean {
  requireCalendarDate(today, 'today');
  requireSafeInteger(horizonDays, 'horizonDays');
  if (horizonDays < 0) {
    throw new Error('horizonDays must be >= 0');
  }
  if (endsOn === null) return false;
  requireCalendarDate(endsOn, 'endsOn');
  const daysUntil = calendarDaysBetween(today, endsOn);
  return daysUntil >= 0 && daysUntil <= horizonDays;
}

export interface BuildCreditMonitorAccountRowInput {
  readonly accountId: string;
  readonly orgId: string | null;
  readonly orgName: string | null;
  readonly layers: CreditLayerUsage;
  readonly liveDebtUsdMicros: number;
  readonly today: string;
  /** Earliest dunning `ar_overdue` due date; null if dunning did not report overdue. */
  readonly overdueDueDate: string | null;
  /** Earliest dunning `ar_due_soon` due date; null if dunning did not report due-soon. */
  readonly dueSoonDueDate: string | null;
  readonly overdueStage: CreditOverdueStage | null;
  readonly exemption: CreditOverdueExemptionView | null;
  readonly temporaryExpiresOn: string | null;
}

export function buildCreditMonitorAccountRow(
  input: BuildCreditMonitorAccountRowInput,
): CreditMonitorAccountRow {
  requireCalendarDate(input.today, 'today');
  const liveDebt = requireNonNegativeSafeInteger(
    input.liveDebtUsdMicros,
    'liveDebtUsdMicros',
  );
  const used = creditUsedUsdMicros(input.layers);
  const cap = creditCapUsdMicros(input.layers);
  const overdueDueDate =
    input.overdueDueDate === null
      ? null
      : requireCalendarDate(input.overdueDueDate, 'overdueDueDate');
  const dueSoonDueDate =
    input.dueSoonDueDate === null
      ? null
      : requireCalendarDate(input.dueSoonDueDate, 'dueSoonDueDate');
  const temporaryExpiresOn =
    input.temporaryExpiresOn === null
      ? null
      : requireCalendarDate(input.temporaryExpiresOn, 'temporaryExpiresOn');

  const isOverdue = overdueDueDate !== null;
  const dueSoon = !isOverdue && dueSoonDueDate !== null;
  const queue = classifyCreditMonitorQueue({
    overdue: isOverdue,
    dueSoon,
    usedUsdMicros: used,
    capUsdMicros: cap,
    overLimitUsdMicros: input.layers.over_limit_usd_micros,
  });

  const overdue: CreditMonitorOverdueFact | null =
    overdueDueDate === null
      ? null
      : {
          days_past_due: calendarDaysBetween(overdueDueDate, input.today),
          due_date: overdueDueDate,
          stage: input.overdueStage,
          exemption: input.exemption,
        };

  const dueSoonFact: CreditMonitorDueSoonFact | null =
    dueSoon && dueSoonDueDate !== null
      ? {
          due_date: dueSoonDueDate,
          days_until_due: calendarDaysBetween(input.today, dueSoonDueDate),
        }
      : null;

  let nextDueDate: string | null = null;
  if (overdueDueDate !== null) nextDueDate = overdueDueDate;
  else if (dueSoonDueDate !== null) nextDueDate = dueSoonDueDate;
  else if (temporaryExpiresOn !== null) nextDueDate = temporaryExpiresOn;

  return {
    account_id: input.accountId,
    org_id: input.orgId,
    org_name: input.orgName,
    layers: input.layers,
    live_debt_usd_micros: liveDebt,
    queue,
    overdue,
    due_soon: dueSoonFact,
    exemption: input.exemption,
    temporary_expires_on: temporaryExpiresOn,
    next_due_date: nextDueDate,
  };
}

/**
 * Totals from the same row list the table shows. Overdue amount is the
 * sum of those rows' live debt — never `closing_ar`.
 *
 * These totals are the visible-scope FULL set. If the list is later
 * paged, keep summarizing the whole visible book — never the current
 * page. Filter/sort in the console is a display cut; it does not
 * change this function's input.
 */
export function summarizeCreditMonitor(
  accounts: readonly CreditMonitorAccountRow[],
): CreditMonitorSummary {
  let totalLimit = 0;
  let used = 0;
  let overdueUsd = 0;
  let overdueCount = 0;
  let dueSoonCount = 0;
  let temporaryExpiringCount = 0;

  for (const row of accounts) {
    totalLimit += creditCapUsdMicros(row.layers);
    used += creditUsedUsdMicros(row.layers);
    if (row.overdue !== null) {
      overdueCount += 1;
      overdueUsd += row.live_debt_usd_micros;
    }
    if (row.due_soon !== null) {
      dueSoonCount += 1;
    }
    if (row.temporary_expires_on !== null) {
      temporaryExpiringCount += 1;
    }
  }

  return {
    total_limit_usd_micros: totalLimit,
    used_usd_micros: used,
    overdue_usd_micros: overdueUsd,
    overdue_account_count: overdueCount,
    due_soon_account_count: dueSoonCount,
    temporary_expiring_account_count: temporaryExpiringCount,
  };
}

const QUEUE_RANK: Record<CreditMonitorQueue, number> = {
  overdue: 0,
  due_soon: 1,
  high_usage: 2,
  other: 3,
};

function displayName(row: CreditMonitorAccountRow): string {
  if (row.org_name !== null && row.org_name.length > 0) return row.org_name;
  if (row.org_id !== null) return row.org_id;
  return row.account_id;
}

function compareOverduePinned(left: CreditMonitorAccountRow, right: CreditMonitorAccountRow): number {
  const leftDays = left.overdue?.days_past_due ?? 0;
  const rightDays = right.overdue?.days_past_due ?? 0;
  if (leftDays !== rightDays) return rightDays - leftDays;
  if (left.live_debt_usd_micros !== right.live_debt_usd_micros) {
    return right.live_debt_usd_micros - left.live_debt_usd_micros;
  }
  if (left.account_id === right.account_id) return 0;
  return left.account_id < right.account_id ? -1 : 1;
}

function compareRest(
  left: CreditMonitorAccountRow,
  right: CreditMonitorAccountRow,
  sort: CreditMonitorSort,
): number {
  if (sort === 'name') {
    const byName = displayName(left).localeCompare(displayName(right));
    if (byName !== 0) return byName;
  } else if (sort === 'usage') {
    const leftUsed = creditUsedUsdMicros(left.layers);
    const rightUsed = creditUsedUsdMicros(right.layers);
    if (leftUsed !== rightUsed) return rightUsed - leftUsed;
  } else {
    const byQueue = QUEUE_RANK[left.queue] - QUEUE_RANK[right.queue];
    if (byQueue !== 0) return byQueue;
    const leftUsed = creditUsedUsdMicros(left.layers);
    const rightUsed = creditUsedUsdMicros(right.layers);
    if (leftUsed !== rightUsed) return rightUsed - leftUsed;
  }
  if (left.account_id === right.account_id) return 0;
  return left.account_id < right.account_id ? -1 : 1;
}

function restMatchesFilter(row: CreditMonitorAccountRow, filter: CreditMonitorFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'overdue') return false;
  if (filter === 'due_soon') return row.due_soon !== null;
  return row.temporary_expires_on !== null;
}

/**
 * Overdue rows stay first under every sort and filter. Settlement is
 * the only way they leave the list; this function has no dismiss path.
 *
 * When the list is later paged, overdue rows must still occupy page
 * one. "Still first after sort/filter" is otherwise empty once the
 * window is a page.
 */
export function sortCreditMonitorAccounts(
  accounts: readonly CreditMonitorAccountRow[],
  options: {
    readonly filter?: CreditMonitorFilter;
    readonly sort?: CreditMonitorSort;
  } = {},
): CreditMonitorAccountRow[] {
  const filter = options.filter ?? 'all';
  const sort = options.sort ?? 'default';
  if (!isCreditMonitorFilter(filter)) {
    throw new Error(`unknown credit-monitor filter ${JSON.stringify(filter)}`);
  }
  if (!isCreditMonitorSort(sort)) {
    throw new Error(`unknown credit-monitor sort ${JSON.stringify(sort)}`);
  }

  const overdue: CreditMonitorAccountRow[] = [];
  const rest: CreditMonitorAccountRow[] = [];
  for (const row of accounts) {
    if (row.overdue !== null) overdue.push(row);
    else rest.push(row);
  }

  const filteredRest: CreditMonitorAccountRow[] = [];
  for (const row of rest) {
    if (restMatchesFilter(row, filter)) filteredRest.push(row);
  }

  overdue.sort(compareOverduePinned);
  filteredRest.sort((left, right) => compareRest(left, right, sort));
  return [...overdue, ...filteredRest];
}
